import React from "react";
import ReactDOM from "react-dom";

/**
 * Formats a number with comma-separated thousands and fixed decimal precision.
 * e.g. formatOrderBookNumber(78382.177981, 6) -> "78,382.177981"
 */
export const formatOrderBookNumber = (val, precision = 6) => {
  if (val === null || val === undefined || isNaN(val)) return "0";
  const num = Number(val);
  const p = Math.max(0, Math.min(12, Number(precision) || 0));
  const fixed = num.toFixed(p);
  const [intPart, decPart] = fixed.split(".");
  const formattedInt = Number(intPart).toLocaleString("en-US");
  return decPart !== undefined && p > 0 ? `${formattedInt}.${decPart}` : formattedInt;
};

const OrderBookTooltip = ({
  hoverInfo,
  baseCurrency = "BTC",
  quoteCurrency = "USDT",
  theme = "dark",
}) => {
  if (!hoverInfo || !hoverInfo.rect) return null;

  const { rect, avgPrice, sumBase, sumQuote } = hoverInfo;

  const TOOLTIP_WIDTH = 220;
  const TOOLTIP_HEIGHT = 76;
  const OFFSET = 8;

  const rowCenterY = rect.top + rect.height / 2;
  const rowRight = rect.right;
  const rowLeft = rect.left;

  // Decide whether tooltip fits on the right
  const hasSpaceRight =
    window.innerWidth - rowRight >= TOOLTIP_WIDTH + OFFSET + 10;

  let left = 0;
  let positionSide = "right";

  if (hasSpaceRight) {
    left = rowRight + OFFSET;
    positionSide = "right";
  } else {
    left = rowLeft - TOOLTIP_WIDTH - OFFSET;
    positionSide = "left";
  }

  let top = rowCenterY - TOOLTIP_HEIGHT / 2;
  top = Math.max(10, Math.min(window.innerHeight - TOOLTIP_HEIGHT - 10, top));

  const arrowTop = Math.max(10, Math.min(TOOLTIP_HEIGHT - 10, rowCenterY - top));

  const isLight = theme === "light";
  const bg = isLight ? "#ffffff" : "#1e2329";
  const border = isLight ? "#e6e8ea" : "#363c4e";
  const labelColor = isLight ? "#707a8a" : "#848e9c";
  const valueColor = isLight ? "#1e2329" : "#eaecef";

  return ReactDOM.createPortal(
    <div
      className="binance-orderbook-tooltip"
      style={{
        position: "fixed",
        left: `${left}px`,
        top: `${top}px`,
        width: `${TOOLTIP_WIDTH}px`,
        zIndex: 999999,
        pointerEvents: "none",
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: "4px",
        boxShadow: isLight
          ? "0 4px 16px rgba(0, 0, 0, 0.12)"
          : "0 6px 20px rgba(0, 0, 0, 0.5)",
        padding: "8px 12px",
        boxSizing: "border-box",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        userSelect: "none",
        transition: "top 0.04s ease-out, left 0.04s ease-out",
      }}
    >
      {/* Arrow pointing to row */}
      {positionSide === "right" ? (
        <div
          style={{
            position: "absolute",
            left: "-5px",
            top: `${arrowTop}px`,
            transform: "translateY(-50%) rotate(45deg)",
            width: "8px",
            height: "8px",
            backgroundColor: bg,
            borderLeft: `1px solid ${border}`,
            borderBottom: `1px solid ${border}`,
          }}
        />
      ) : (
        <div
          style={{
            position: "absolute",
            right: "-5px",
            top: `${arrowTop}px`,
            transform: "translateY(-50%) rotate(45deg)",
            width: "8px",
            height: "8px",
            backgroundColor: bg,
            borderRight: `1px solid ${border}`,
            borderTop: `1px solid ${border}`,
          }}
        />
      )}

      {/* Row 1: Avg Price */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "4px",
          lineHeight: "16px",
        }}
      >
        <span
          style={{
            color: labelColor,
            fontSize: "12px",
            whiteSpace: "nowrap",
            marginRight: "8px",
          }}
        >
          Avg Price:
        </span>
        <span
          style={{
            color: valueColor,
            fontSize: "12px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          ≈ {avgPrice}
        </span>
      </div>

      {/* Row 2: Sum Base Asset (e.g. Sum BTC) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "4px",
          lineHeight: "16px",
        }}
      >
        <span
          style={{
            color: labelColor,
            fontSize: "12px",
            whiteSpace: "nowrap",
            marginRight: "8px",
          }}
        >
          Sum {baseCurrency?.toUpperCase() || "BTC"}:
        </span>
        <span
          style={{
            color: valueColor,
            fontSize: "12px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {sumBase}
        </span>
      </div>

      {/* Row 3: Sum Quote Asset (e.g. Sum USDT) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          lineHeight: "16px",
        }}
      >
        <span
          style={{
            color: labelColor,
            fontSize: "12px",
            whiteSpace: "nowrap",
            marginRight: "8px",
          }}
        >
          Sum {quoteCurrency?.toUpperCase() || "USDT"}:
        </span>
        <span
          style={{
            color: valueColor,
            fontSize: "12px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {sumQuote}
        </span>
      </div>
    </div>,
    document.body
  );
};

export default OrderBookTooltip;

